import { useEffect, useMemo } from "react";
import { CalcSteps } from "@/components/calc/CalcSteps";
import { ResultsTable, CALC_PRINT_CSS } from "@/components/calc/ResultsTable";
import { ExportBar } from "@/components/calc/ExportBar";
import { CalcHistory } from "@/components/calc/CalcHistory";
import { recordCalculation } from "@/lib/calc/history";
import { stepsToText } from "@/lib/calc/steps";
import { tableToCsv, tableToTsv } from "@/lib/calc/table";
import type { LoanResult } from "./loan";
import { calendarYearRows, formatFullDate, formatMonthYear } from "./paymentDates";
import type { YMD } from "./dates";
import {
  biweeklyPlan, costGrowth, costGrowthColumns, mortgageHistorySummary, mortgageSteps, paymentBreakdown, scheduleCsvColumns, scheduleCsvRows,
  type CostGrowthInput, type MortgageStepData, type Money,
} from "./mortgageWorking";

const COLORS = ["#2563eb", "#d97706", "#059669", "#7c3aed", "#dc2626", "#0891b2"];

/** Donut of the monthly payment. The legend beside it carries every amount and percent, so the colours are never the only signal. */
function PaymentDonut({ parts, total, money }: { parts: { key: string; label: string; value: number }[]; total: number; money: Money }) {
  const slices = paymentBreakdown(parts);
  const R = 42, C = 2 * Math.PI * R;
  let offset = 0;
  const alt = `Donut chart of the monthly payment of ${money(total)}: ${slices.map((s) => `${s.label} ${money(s.value)} (${s.pct.toFixed(1)}%)`).join(", ")}.`;
  return (
    <figure className="flex flex-col sm:flex-row items-center gap-4" data-testid="mg-donut">
      <svg viewBox="0 0 120 120" role="img" aria-label={alt} className="w-36 h-36 shrink-0">
        <g transform="rotate(-90 60 60)">
          {slices.map((s, i) => {
            const len = (s.pct / 100) * C;
            const el = <circle key={s.key} cx="60" cy="60" r={R} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="16" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset}><title>{`${s.label}: ${money(s.value)} (${s.pct.toFixed(1)}%)`}</title></circle>;
            offset += len;
            return el;
          })}
        </g>
        <text x="60" y="64" textAnchor="middle" fontSize="11" fontWeight="700" fill="currentColor">{money(total)}</text>
      </svg>
      <figcaption className="text-sm w-full">
        <p className="font-medium mb-1">Where each month's payment goes</p>
        <ul className="space-y-1">
          {slices.map((s, i) => (
            <li key={s.key} className="flex items-center gap-2" data-testid="mg-donut-item">
              <span aria-hidden="true" className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="flex-1">{s.label}</span><span>{money(s.value)}</span><span className="text-muted-foreground w-14 text-right">{s.pct.toFixed(1)}%</span>
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  );
}

/**
 * Under the Mortgage Calculator's result: payment breakdown, working, biweekly comparison, cost growth by year and the gated export bar.
 * Loaded on demand (React.lazy), so none of this is in the tool's first load.
 */
export default function MortgageExtras({ d, res, first, money, gate, growth, parts, biweeklyOn }: {
  d: MortgageStepData; res: LoanResult; first: YMD; money: Money; gate: () => Promise<boolean>;
  growth: CostGrowthInput; parts: { key: string; label: string; value: number }[]; biweeklyOn: boolean;
}) {
  const steps = useMemo(() => mortgageSteps(d, money), [d, money]);
  const bw = useMemo(() => biweeklyPlan(d.loan, d.annualPct, Math.round(d.years * 12), first), [d.loan, d.annualPct, d.years, first]);
  const costRows = useMemo(() => costGrowth(res.rows, growth), [res.rows, growth]);
  const costCols = useMemo(() => costGrowthColumns(money), [money]);
  const grows = growth.taxGrowthPct > 0 || growth.insGrowthPct > 0 || growth.hoaGrowthPct > 0 || growth.other > 0;
  const csvRows = useMemo(() => scheduleCsvRows(res.rows, first), [res.rows, first]);
  const yearly = useMemo(() => calendarYearRows(res.rows, first), [res, first]);

  const summary = mortgageHistorySummary(d.price, d.annualPct, d.years, d.total, money);
  useEffect(() => {
    const t = setTimeout(() => recordCalculation("mortgage-calculator", summary), 800);
    return () => clearTimeout(t);
  }, [summary]);

  const reportText = [
    "Mortgage estimate", summary, "", "How this was calculated", stepsToText(steps), "",
    ...(bw ? ["Biweekly payments (principal and interest only)", `${money(bw.payment)} every 2 weeks: ${bw.payments} payments, interest ${money(bw.totalInterest)} vs ${money(bw.monthlyInterest)} monthly, ${money(bw.interestSaved)} saved${bw.payoff ? `, paid off ${formatFullDate(bw.payoff)} instead of ${formatFullDate(bw.monthlyPayoff!)}` : ""}`, ""] : []),
    ...(grows ? ["Monthly cost by loan year", tableToTsv(costRows, costCols), ""] : []),
    "Yearly schedule", ["Year\tPrincipal\tInterest\tBalance", ...yearly.map((y) => `${y.year}\t${money(y.principal)}\t${money(y.interest)}\t${money(y.balance)}`)].join("\n"), "",
    "Estimate only. Confirm with your lender. Calculated with FreeDailyPro.com",
  ].join("\n");

  return (
    <div className="space-y-6 calc-print-area">
      <style>{CALC_PRINT_CSS}</style>
      <section aria-label="Payment breakdown" className="rounded-xl border bg-card p-4"><PaymentDonut parts={parts} total={d.total} money={money} /></section>
      <CalcSteps steps={steps} />

      {biweeklyOn && bw && (
        <section aria-label="Biweekly payments" className="rounded-xl border bg-card p-4 space-y-2" data-testid="mg-biweekly">
          <h3 className="text-base font-semibold">Biweekly payments</h3>
          <p className="text-sm">Paying <strong>{money(bw.payment)}</strong> (half of the {money(d.payment)} principal and interest) every 2 weeks means 26 half-payments, the same as 13 monthly payments a year.</p>
          <ul className="text-sm space-y-1">
            <li>Paid off in <strong>{bw.payments}</strong> biweekly payments{bw.payoff ? <> on <strong data-testid="mg-bw-payoff">{formatFullDate(bw.payoff)}</strong> instead of {formatMonthYear(bw.monthlyPayoff!)}</> : ""}, about {bw.yearsLabel} sooner.</li>
            <li>Interest <strong>{money(bw.totalInterest)}</strong> instead of {money(bw.monthlyInterest)}: <strong data-testid="mg-bw-saved">{money(bw.interestSaved)}</strong> saved.</li>
          </ul>
          <p className="text-xs text-muted-foreground">Model: interest builds up every 14 days at the yearly rate / 26, and each payment is posted when made. Principal and interest only, compared with the plain monthly schedule (your extra payments are not included). Many servicers hold biweekly payments and post them monthly, which saves less, and some charge a fee for a biweekly plan. Ask your lender before you start.</p>
        </section>
      )}

      {grows && (
        <section aria-label="Monthly cost by year" className="space-y-2" data-testid="mg-cost-growth">
          <h3 className="text-base font-semibold">How the monthly cost changes each year</h3>
          <p className="text-xs text-muted-foreground">Principal and interest stay fixed. Property tax, insurance, HOA and maintenance rise by the percentages you entered at the start of each loan year (maintenance and other costs rise with the insurance percentage). Mortgage insurance ends as it does in the schedule.</p>
          <ResultsTable caption="Monthly cost by loan year" rows={costRows} columns={costCols} rowKey={(r) => r.year} maxHeightClass="max-h-72" />
        </section>
      )}

      <section aria-label="Export" className="space-y-2">
        <p className="text-sm font-medium no-print print:hidden">Take it with you</p>
        <ExportBar gate={gate} text={reportText} csv={tableToCsv(csvRows, scheduleCsvColumns)} filename="mortgage-schedule.csv" label="report" />
        <p className="text-xs text-muted-foreground no-print print:hidden">The CSV has the yearly summary first, then every month.</p>
      </section>
      <CalcHistory tool="mortgage-calculator" />
    </div>
  );
}
